import { DatePicker } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  Grid,
  useEventCallback,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { PmiDialog } from 'shell/components/shared/dialog';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import PmiTextField from 'shell/components/shared/text-field';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';

export interface PrintUnavailabilityVM {
  dateStartExtraction?: DateTime | null;
  dateFinishExtraction?: DateTime | null;
}

interface PrintUnavailabilityProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrintUnavailability = ({ isOpen, onClose }: PrintUnavailabilityProps): React.ReactElement => {
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const printdata = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/elenco-irreperibili-prefettura`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((data: PrintUnavailabilityVM) => {
    (
      printdata({
        data: {
          dataInizioEstrazione: data.dateStartExtraction?.toFormat('yyyy-MM-dd') || '',
          dataFineEstrazione: data.dateFinishExtraction?.toFormat('yyyy-MM-dd') || '',
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      const pdfCertificate = response?.data;
      if (pdfCertificate) {
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Riepilogo stampa',
            open: true,
          });
          onClose();
        });
      }
    });
  });

  const formContext = useFormik<PrintUnavailabilityVM>({
    initialValues: {
      dateStartExtraction: null,
      dateFinishExtraction: null,
    },
    validationSchema: yup.object().shape({
      dateStartExtraction: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .max(
          yup.ref('dateFinishExtraction'),
          'Data di inizio estrazione deve essere minore alla data di fine estrazione'
        )
        .required('Il campo è obbligatorio'),
      dateFinishExtraction: yup
        .date()
        .min(
          yup.ref('dateStartExtraction'),
          'Data di fine estrazione deve essere maggiore alla data di inizio estrazione '
        )
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
    }),
    onSubmit,
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Riepilogo Stampa"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
      <PmiDialog
        fullScreen={fullScreen}
        open={isOpen}
        onClose={onClose}
        aria-labelledby="close-modal"
        aria-describedby="close-modal"
      >
        <PmiDialogTitle id="close-modal-title" backgroundColor="primary" color="white">
          Stampa elenco irreperibili per prefettura
        </PmiDialogTitle>
        <DialogContent style={{ padding: '30px 24px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DatePicker
                label="Data di inizio estrazione pratiche *"
                aria-label="Data di inizio estrazione pratiche"
                value={formContext.values?.dateStartExtraction || null}
                onChange={(date) => formContext.setFieldValue('dateStartExtraction', date)}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="dateStartExtraction"
                    fullWidth
                    error={
                      formContext.touched?.dateStartExtraction &&
                      Boolean(formContext.errors?.dateStartExtraction)
                    }
                    helperText={formContext.errors?.dateStartExtraction}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                label="Data di fine estrazione pratiche *"
                aria-label="Data di fine estrazione pratiche"
                value={formContext.values?.dateFinishExtraction || null}
                onChange={(date) => formContext.setFieldValue('dateFinishExtraction', date)}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="dateFinishExtraction"
                    fullWidth
                    error={
                      formContext.touched?.dateFinishExtraction &&
                      Boolean(formContext.errors?.dateFinishExtraction)
                    }
                    helperText={formContext.errors?.dateFinishExtraction}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <PmiButton
            variant="contained"
            color="cdmBrand"
            type="button"
            onClick={() => formContext.handleSubmit()}
          >
            Stampa
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="primary"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onClose();
            }}
          >
            Annulla
          </PmiButton>
        </DialogActions>
      </PmiDialog>
    </>
  );
};

PrintUnavailability.displayName = 'Print Unavailability';

export default PrintUnavailability;
